import React, { useContext, useState } from "react";
import { Alert, Button, IconButton, Stack } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { OrganizationContext } from "../../contexts/OrganizationContext";
import { ONBOARDING_PATH } from "../../config/navigation";
import { dismissReminder, isReminderDismissed } from "../../utils/onboardingCache";
import { common } from "../../i18n";

// Onboarding never hard-gates the app, so this reminder is the whole offer for an organisation still unanswered.
const OnboardingReminderBanner: React.FC = () => {
  const { t } = useTranslation([common]);
  const orgContext = useContext(OrganizationContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const organizationRef = orgContext?.organization?._id;
  const isPending = orgContext?.onboardingStatus === "pending";
  // Layout remounts per page, so a dismiss held only in state would come straight back on the next navigation.
  const alreadyDismissed = dismissed || (!!organizationRef && isReminderDismissed(organizationRef));

  // Layout mounts this on every page, so without this it would also greet the user on the page it sent them to.
  if (!isPending || alreadyDismissed || location.pathname === ONBOARDING_PATH) return null;

  const onDismiss = () => {
    if (organizationRef) dismissReminder(organizationRef);
    setDismissed(true);
  };

  return (
    <Alert
      severity="info"
      // MUI drops its own close button whenever `action` is set, so the dismiss has to live inside the action.
      action={
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button
            variant="contained"
            size="small"
            // Carries the origin so the wizard can put the user back where they were, rather than at the org picker.
            onClick={() =>
              // Search and hash included: a page whose state lives in the query would come back stripped without them.
              navigate(ONBOARDING_PATH, {
                state: { from: `${location.pathname}${location.search}${location.hash}` },
              })
            }
            endIcon={<ArrowForwardIcon />}
            // light pill, not a dark block — white fill + navy text keeps it on-brand and AA-accessible
            sx={{
              backgroundColor: "var(--white)",
              color: "var(--dark)",
              border: "1px solid var(--green)",
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              boxShadow: "0 2px 8px var(--green-20)",
              "& .MuiButton-endIcon": { ml: 0.5 },
              "&:hover": { backgroundColor: "var(--white)", boxShadow: "0 3px 10px var(--green-20)" },
            }}
          >
            {t("Onboarding-Reminder-Action")}
          </Button>
          <IconButton size="small" aria-label={t("Close")} onClick={onDismiss} sx={{ color: "var(--dark)" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      }
      sx={{
        // light + on-brand — a dark bar clashed with the airy dashboard.
        background: "linear-gradient(to right, var(--green3) 0%, var(--green3) 60%, var(--green-20) 100%)",
        color: "var(--dark)",
        borderRadius: 0,
        borderLeft: "4px solid var(--green-dark)",
        // MUI top-aligns the action by default; this puts icon, message and CTA on one axis
        alignItems: "center",
        "& .MuiAlert-icon": { color: "var(--green)" },
        "& .MuiAlert-action": { alignItems: "center", mt: 0, pt: 0 },
      }}
    >
      {t("Onboarding-Reminder-Message")}
    </Alert>
  );
};

export default OnboardingReminderBanner;
